import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  SilentRequest
} from '@azure/msal-browser';

const RENEW_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
const RETRY_RENEWAL_MS = 30 * 1000;

interface TokenRenewalHandlers {
  onToken: (accessToken: string) => void;
  onInteractionRequired: () => void;
  onFailure: (failure: unknown) => void;
}

type Attempt = { result: AuthenticationResult } | { failure: unknown };
type Reason = 'sign-in' | 'renewal' | 'expiry';

const renewalDelay = ({ expiresOn }: AuthenticationResult): number =>
  expiresOn ? Math.max(expiresOn.getTime() - Date.now() - RENEW_BEFORE_EXPIRY_MS, RETRY_RENEWAL_MS) : RETRY_RENEWAL_MS;

// Tally hands the token to the API client and the socket rather than asking per request, so it is renewed ahead of
// expiry. Returns the function that stops renewing.
export const keepTokenFresh = (
  instance: IPublicClientApplication,
  request: SilentRequest,
  handlers: TokenRenewalHandlers
): (() => void) => {
  let cancelled = false;
  let renewal: ReturnType<typeof setTimeout> | undefined;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  let inFlight: Promise<Attempt> | null = null;
  let installed: AuthenticationResult | null = null;

  const stop = () => {
    cancelled = true;
    clearTimeout(renewal);
    clearTimeout(expiry);
  };

  const schedule = (delay: number) => {
    clearTimeout(renewal);
    renewal = setTimeout(() => void acquire('renewal'), delay);
  };

  // A laptop waking from sleep fires the renewal and expiry timers together, so both share one request.
  const attempt = (): Promise<Attempt> => {
    inFlight ??= instance
      .acquireTokenSilent(request)
      .then(
        (result): Attempt => ({ result }),
        (failure: unknown): Attempt => ({ failure })
      )
      .finally(() => (inFlight = null));
    return inFlight;
  };

  const install = (result: AuthenticationResult) => {
    if (result === installed) return;
    installed = result;
    handlers.onToken(result.accessToken);
    clearTimeout(expiry);
    // Past this point the bearer is dead; the expiry makes one last silent attempt before ending the session.
    if (result.expiresOn) expiry = setTimeout(() => void acquire('expiry'), result.expiresOn.getTime() - Date.now());
    schedule(renewalDelay(result));
  };

  const acquire = async (reason: Reason) => {
    const outcome = await attempt();
    if (cancelled) return;
    if ('result' in outcome) {
      install(outcome.result);
      return;
    }
    if (outcome.failure instanceof InteractionRequiredAuthError) {
      handlers.onInteractionRequired();
    } else if (reason === 'renewal') {
      // A blip while renewing must not sign out a user whose current token still has minutes left.
      schedule(RETRY_RENEWAL_MS);
    } else {
      stop();
      handlers.onFailure(outcome.failure);
    }
  };

  void acquire('sign-in');
  return stop;
};
